import { create } from 'zustand'

/**
 * A counter that ticks every time the effective zone actually changes.
 *
 * The zone itself is module state — formatters are plain functions, not
 * hooks — so nothing re-renders when it moves. Yet everything already on
 * screen was formatted in the old zone, and in production the React Compiler
 * memoises components on their inputs, so even a parent re-render would not
 * reach a row whose props didn't change. The only reliable way to repaint the
 * page in the new zone is to remount it, and that is what the epoch is for:
 * the dashboard keys its outlet on it, the same way it already keys the outlet
 * per workspace tab.
 *
 * Never bumped on the initial resolve (nothing is mounted yet) or when the
 * resolved zone equals the current one (every in-app navigation re-runs the
 * resolver from cache).
 */
interface TimezoneEpochState {
  epoch: number
  bump: () => void
}

export const useTimezoneEpochStore = create<TimezoneEpochState>()((set) => ({
  epoch: 0,
  bump: () => set((state) => ({ epoch: state.epoch + 1 })),
}))

/** Subscribe to zone changes. Compose the value into a `key` to remount. */
export function useTimezoneEpoch(): number {
  return useTimezoneEpochStore((state) => state.epoch)
}
